import { campaigns, opportunities, socialPosts, demandMetrics } from '../data/demandData'
import { demandTrend, railProjects, railTasks, socialSnapshot } from '../data/demandBriefing'
import { eddyIncomingBatches, onePeopleIncomingBatches } from '../data/inventorySubmissions'
import { defaultRecoveryRules, recoveryChannels } from '../data/recoveryRules'
import { initialCompanies, initialActivity } from '../components/PipelineDashboard'
import { eddyChannels, contentPushes, spendByMonth } from '../components/MarketingDashboard'
import { brands as messageBrands, initialThreads } from '../components/BrandMessagesDashboard'

export type BrandSnapshot = {
  name: string
  received: string
  live: string
  sold: string
  sales: string
  msrp: string
  maxOpportunity: string
}

export type NisoloFacts = {
  submissions: Array<{ name: string; arrived: string; received: number; msrp: number; estimatedRecovery: number }>
  totalReceived: number
  totalMsrp: number
  totalEstimatedRecovery: number
  orderUnits: number
  orderSales: number
  aur: number
  channels: string[]
  topProducts: Array<{ name: string; units: number; sales: number }>
} | null

const viewLabels: Record<string, string> = {
  carli: 'Home',
  inventory: 'Inventory',
  performance: 'Recovery',
  marketing: 'Marketing',
  controls: 'Settings',
  messages: 'Messages',
  finances: 'Payouts',
  demand: 'Demand',
  pipeline: 'Pipeline',
}

export function viewLabelFor(view: string) {
  return viewLabels[view] ?? 'Portal'
}

const money = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`

/**
 * Carli sees the whole portal, not just the screen the user happens to be on.
 * Every section of the product is summarized here so she can answer across
 * brands, inventory, demand, marketing, messages, and the sales pipeline.
 */
export function buildPortalContext({
  activeView,
  selectedOwner,
  brandRoster,
  brandSnapshots,
  portfolio,
  nisolo,
}: {
  activeView: string
  selectedOwner: string
  brandRoster: string[]
  brandSnapshots: BrandSnapshot[]
  portfolio: BrandSnapshot
  nisolo: NisoloFacts
}): string {
  const sections: string[] = []

  sections.push(
    [
      'WHERE THE USER IS',
      `Screen: ${viewLabelFor(activeView)}. Brand scope selector: ${selectedOwner || 'All brands'}.`,
      'You can reference and reason about ANY part of the portal below, not just this screen. If a question is about another area, answer it directly rather than telling the user to switch screens.',
    ].join('\n'),
  )

  sections.push(
    [
      'PORTFOLIO ROSTER',
      `${brandRoster.length} brands are onboarded in the portal. Brands with live recovery data today: ${brandSnapshots
        .map((brand) => brand.name)
        .join(', ')}.`,
      `Full roster: ${brandRoster.join(', ')}.`,
    ].join('\n'),
  )

  sections.push(
    [
      'RECOVERY POSITION BY BRAND',
      `Portfolio total — ${portfolio.received} units uploaded, ${portfolio.live} live units, ${portfolio.sold} units sold, ${portfolio.sales} recovery sales, ${portfolio.msrp} total MSRP value, ${portfolio.maxOpportunity} estimated remaining opportunity.`,
      ...brandSnapshots.map(
        (brand) =>
          `${brand.name} — ${brand.received} uploaded, ${brand.live} live, ${brand.sold} sold, ${brand.sales} sales, ${brand.msrp} MSRP, ${brand.maxOpportunity} opportunity.`,
      ),
    ].join('\n'),
  )

  sections.push(
    [
      'INVENTORY SUBMISSIONS',
      'Eddy: ' +
        eddyIncomingBatches
          .map((batch) => `${batch.name} (uploaded ${batch.arrived}, ${batch.received.toLocaleString('en-US')} units)`)
          .join('; '),
      '1 People: ' +
        onePeopleIncomingBatches
          .map((batch) => `${batch.name} (uploaded ${batch.arrived}, ${batch.received.toLocaleString('en-US')} units)`)
          .join('; '),
      nisolo
        ? 'Nisolo (live CSV data): ' +
          nisolo.submissions
            .map(
              (submission) =>
                `${submission.name} (uploaded ${submission.arrived}, ${submission.received.toLocaleString('en-US')} units, ${money(
                  submission.msrp,
                )} MSRP, ${money(submission.estimatedRecovery)} predicted recovery)`,
            )
            .join('; ')
        : 'Nisolo submissions are still loading.',
    ].join('\n'),
  )

  if (nisolo) {
    sections.push(
      [
        'NISOLO REALIZED SALES (from the live order file)',
        `${nisolo.orderUnits.toLocaleString('en-US')} units sold for ${money(nisolo.orderSales)} at an average unit revenue of ${money(nisolo.aur)}.`,
        `Selling channels: ${nisolo.channels.join(', ')}.`,
        `Top products by revenue: ${nisolo.topProducts
          .map((product) => `${product.name} (${product.units} units, ${money(product.sales)})`)
          .join('; ')}.`,
      ].join('\n'),
    )
  }

  sections.push(
    [
      'RECOVERY RULES',
      `Default account rules: channels ${defaultRecoveryRules.channels.join(', ')}; index price ${defaultRecoveryRules.indexPrice}% of MSRP; sales horizon ${defaultRecoveryRules.horizonMonths} months; live selling held until month ${defaultRecoveryRules.liveSellingAfterMonth}.`,
      `Available channels: ${recoveryChannels.map((channel) => channel.name).join(', ')}.`,
      'Rules can be set per inventory submission, one submission at a time, in Settings.',
    ].join('\n'),
  )

  sections.push(
    [
      'MARKETING',
      `Eddy channel spend — ${eddyChannels
        .map((channel) => `${channel.name}: ${channel.spend} spend, ${channel.revenue} revenue, ${channel.roas} ROAS`)
        .join('; ')}.`,
      `Monthly spend vs attributed revenue — ${spendByMonth
        .map((month) => `${month.label}: ${money(month.spend)} spend / ${money(month.revenue)} revenue`)
        .join('; ')}.`,
      `Content pushes — ${contentPushes.map((push) => `${push.month}: ${push.title} (${push.status})`).join('; ')}.`,
    ].join('\n'),
  )

  sections.push(
    [
      'DEMAND',
      `Headline metrics — ${demandMetrics.map((metric) => `${metric.label}: ${metric.value}`).join('; ')}.`,
      `Opportunities (inventory below forecast) — ${opportunities
        .map(
          (item) =>
            `${item.brand} ${item.title}: ${item.units} units, ${item.sellThrough}% sell-through vs ${item.forecast}% forecast, ${item.recovery} recovery, recommendation "${item.action}" (${item.priority} priority)`,
        )
        .join('; ')}.`,
      `Campaigns — ${campaigns
        .map(
          (campaign) =>
            `${campaign.name} (${campaign.brand}, ${campaign.channel}, ${campaign.status}, ${campaign.window}): ${money(
              campaign.spend,
            )} spend, ${money(campaign.revenue)} revenue, ${campaign.units} units`,
        )
        .join('; ')}.`,
      `Six-month trend — ${demandTrend
        .map((point) => `${point.month}: ${money(point.revenue)} revenue / ${money(point.spend)} spend / ${point.units} units`)
        .join('; ')}.`,
      `Social — ${socialSnapshot.map((stat) => `${stat.label}: ${stat.value}`).join('; ')}. Posts: ${socialPosts
        .map((post) => `${post.channel} "${post.caption}" (${post.status})`)
        .join('; ')}.`,
      `Today's tasks — ${railTasks.map((task) => `${task.title} (${task.owner}, ${task.due})`).join('; ')}.`,
      `Active projects — ${railProjects
        .map((project) => `${project.name} ${project.progress}% (${project.lead}, next: ${project.nextStep})`)
        .join('; ')}.`,
    ].join('\n'),
  )

  sections.push(
    [
      'SALES PIPELINE',
      `Companies — ${initialCompanies
        .map(
          (company) =>
            `${company.name} (${company.type}, stage ${company.stage}, contract ${company.contract}, owner ${company.owner}, value ${company.value}, ${company.location})`,
        )
        .join('; ')}.`,
      `Recent activity — ${initialActivity
        .map((entry) => `${entry.timestamp} ${entry.company} ${entry.kind}: ${entry.summary} (${entry.owner})`)
        .join('; ')}.`,
    ].join('\n'),
  )

  sections.push(
    [
      'BRAND MESSAGES',
      `Accounts with threads: ${messageBrands.map((brand) => brand.name).join(', ')}.`,
      `Threads — ${initialThreads
        .map((thread) => {
          const brand = messageBrands.find((item) => item.id === thread.brandId)?.name ?? thread.brandId
          return `${brand}: "${thread.title}" (${thread.category}, updated ${thread.updatedAt}${
            thread.waitingForReply ? ', awaiting our reply' : ''
          }) — ${thread.preview}`
        })
        .join('; ')}.`,
    ].join('\n'),
  )

  return sections.join('\n\n')
}
